import {
  LABEL_INCORRECT,
  LABEL_LEARNING,
  LABEL_MASTERED,
  LABEL_NEW,
  MASTERY_LEVEL_MAX,
  MASTERY_LEVEL_MIN,
  MASTERY_THRESHOLD_MASTERED,
  PRACTICE_MIX_LEARNING,
  PRACTICE_MIX_MASTERED,
  PRACTICE_MIX_NEW,
  PRACTICE_MIX_REVIEW,
} from "./constants.js";

/**
 * Fisher-Yates shuffle algorithm.
 * @param {Array} array - The array to shuffle.
 * @returns {Array} - The shuffled array (cloned to avoid mutation).
 */
export const shuffleArray = (array) => {
  const newArr = [...array];
  for (let i = newArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
  }
  return newArr;
};

/**
 * Sorts questions by how recently they were seen.
 * "Assurance that we see all questions" via LRU.
 * @param {Array} questions - Array of question objects.
 * @param {Object} historyMap - { [id]: [{date: number}] }
 * @returns {Array} - Sorted array, most recent first (for pop() to get oldest).
 */
export const sortByRecency = (questions, historyMap) => {
  const now = Date.now();
  return questions
    .map((q) => {
      const history = historyMap[q.id] || [];
      const lastSeen = history.length > 0 ? (history[history.length - 1].date || 0) : 0;
      const age = now - lastSeen;
      // Add +/- 20% jitter to the age to avoid strict oldest-to-newest ordering
      const jitteredAge = age * (0.8 + Math.random() * 0.4);
      const jitteredLastSeen = now - jitteredAge;
      return { q, jitteredLastSeen };
    })
    .sort((a, b) => b.jitteredLastSeen - a.jitteredLastSeen)
    .map((item) => item.q);
};

/**
 * Calculates the mastery level (0-5) based on history.
 * @param {Array<{correct: boolean}>} history
 * @returns {number}
 */
export const calculateMasteryLevel = (history) => {
  if (!history || history.length === 0) return MASTERY_LEVEL_MIN;

  // Find most recent incorrect answer to determine "Needs Review" status
  let lastIncorrectIdx = -1;
  for (let i = history.length - 1; i >= 0; i--) {
    if (!history[i].correct) {
      lastIncorrectIdx = i;
      break;
    }
  }

  // If there's an incorrect answer in history
  if (lastIncorrectIdx !== -1) {
    const correctSince = history.length - 1 - lastIncorrectIdx;
    // "Purgatory": Needs 5 correct answers to exit Level 1
    const penaltyTurns = 5;

    if (correctSince <= penaltyTurns) {
      return 1; // Needs Review
    }

    // If we have more than 5 correct answers, calculate effective streak.
    // We want the 6th correct answer to correspond to Streak 1 (Level 2).
    // Streak = Total Correct Since - Penalty
    // e.g., 6 correct - 5 = 1 (Level 2)
    const effectiveStreak = correctSince - penaltyTurns;

    if (effectiveStreak === 1) return 2;
    if (effectiveStreak === 2) return 3;
    if (effectiveStreak === 3) return 4;
    return MASTERY_LEVEL_MAX;
  }

  // No incorrect answer found in history (pure streak)
  // Count consecutive correct answers from the end (which is all of them)
  const streak = history.length;

  if (streak === 1) return 2;
  if (streak === 2) return 3;
  if (streak === 3) return 4;
  return MASTERY_LEVEL_MAX;
};

/**
 * Gets display info for a mastery level.
 * @param {number} level
 * @returns {{label: string, bg: string, text: string}}
 */
export const getMasteryInfo = (level) => {
  if (level === 0) {
    return {
      label: LABEL_NEW,
      colorClass: "m-new",
    };
  }
  if (level === 1) {
    return {
      label: LABEL_INCORRECT,
      colorClass: "m-review",
    };
  }
  if (level === 2 || level === 3) {
    return {
      label: LABEL_LEARNING,
      colorClass: "m-learning",
    };
  }
  return {
    label: LABEL_MASTERED,
    colorClass: "m-mastered",
  };
};
/**
 * Prepares a mixed deck for practice mode based on the "Comfort Zone" algorithm.
 * New Target Mix: 5% Mastered, 15% Review, 10% Learning, 70% New.
 * @param {Array} allQuestions
 * @param {Object} historyMap - { [id]: [{correct: boolean}] }
 * @param {number} size - Desired deck size
 * @param {boolean} reviewOnly - If true, only return questions that need review
 * @returns {Array}
 */
export const preparePracticeDeck = (allQuestions, historyMap, size = 20, reviewOnly = false) => {
  const categories = {
    mastered: [], // Level >= 4
    review: [], // Last was incorrect
    learning: [], // Seen but not mastered (Level 1-3, last correct)
    new: [], // Never seen
  };

  allQuestions.forEach((q) => {
    const history = historyMap[q.id] || [];
    const level = calculateMasteryLevel(history);

    if (level === 0) {
      categories.new.push(q);
    } else if (level === 1) {
      categories.review.push(q);
    } else if (level >= MASTERY_THRESHOLD_MASTERED) {
      categories.mastered.push(q);
    } else {
      categories.learning.push(q);
    }
  });

  if (reviewOnly) {
    // Return all review questions, shuffled, limited to size (if size > 0)
    const reviewShuffled = shuffleArray([...categories.review]);
    return size > 0 ? reviewShuffled.slice(0, size) : reviewShuffled;
  }

  // Sort by recency to ensure we see all questions eventually (LRU)
  // New questions are still purely shuffled for variety.
  const shuffled = {
    mastered: sortByRecency(categories.mastered, historyMap),
    review: sortByRecency(categories.review, historyMap),
    learning: sortByRecency(categories.learning, historyMap),
    new: shuffleArray(categories.new),
  };

  const deck = [];

  // Order of fallback: New -> Learning -> Review -> Mastered
  const pull = (cat, count) => {
    for (let i = 0; i < count; i++) {
      if (shuffled[cat].length > 0) {
        deck.push(shuffled[cat].pop());
      } else if (shuffled.new.length > 0 && cat !== "new") {
        deck.push(shuffled.new.pop());
      } else if (shuffled.learning.length > 0 && cat !== "learning") {
        deck.push(shuffled.learning.pop());
      } else if (shuffled.review.length > 0 && cat !== "review") {
        deck.push(shuffled.review.pop());
      } else if (shuffled.mastered.length > 0 && cat !== "mastered") {
        deck.push(shuffled.mastered.pop());
      }
    }
  };

  const counts = {
    mastered: Math.floor(size * PRACTICE_MIX_MASTERED),
    review: Math.floor(size * PRACTICE_MIX_REVIEW),
    learning: Math.floor(size * PRACTICE_MIX_LEARNING),
  };
  counts.new = size - counts.mastered - counts.review - counts.learning;

  pull("mastered", counts.mastered);
  pull("review", counts.review);
  pull("learning", counts.learning);
  pull("new", counts.new);

  return shuffleArray(deck);
};

/**
 * Calculates statistics per section.
 * @param {Array} allQuestions
 * @param {Object} historyMap
 * @param {number} thresholdMastered
 * @param {number} thresholdLearning
 * @returns {Array<[string, Object]>}
 */
export const calculateSectionStats = (allQuestions, historyMap, thresholdMastered, thresholdLearning) => {
  const sections = {};

  allQuestions.forEach(q => {
    const sectionName = q.section || "Uncategorized";

    if (!sections[sectionName]) {
      sections[sectionName] = { total: 0, mastered: 0, learning: 0, incorrect: 0, new: 0 };
    }

    sections[sectionName].total++;

    const history = historyMap[q.id] || [];
    const mastery = calculateMasteryLevel(history);

    if (mastery >= thresholdMastered) {
      sections[sectionName].mastered++;
    } else if (mastery >= thresholdLearning) {
      sections[sectionName].learning++;
    } else if (mastery === 1) {
      sections[sectionName].incorrect++;
    } else {
      sections[sectionName].new++;
    }
  });

  return Object.entries(sections)
    .sort((a, b) => a[0].localeCompare(b[0]));
};

/**
 * Calculates daily activity statistics.
 * @param {Object} historyMap
 * @param {Array} allQuestions
 * @returns {Array<Object>}
 */
export const calculateDailyStats = (historyMap, allQuestions) => {
  const daily = {};
  const validIds = new Set(allQuestions.map(q => q.id));

  Object.entries(historyMap).forEach(([id, attempts]) => {
    // Only count history for questions that still exist in the database
    if (!validIds.has(id)) return;

    attempts.forEach(attempt => {
      const date = new Date(attempt.date).toISOString().split("T")[0];
      if (!daily[date]) daily[date] = { correct: 0, total: 0 };
      daily[date].total++;
      if (attempt.correct) daily[date].correct++;
    });
  });

  return Object.entries(daily)
    .map(([date, stats]) => ({
      date,
      ...stats,
      rate: Math.round((stats.correct / stats.total) * 100),
    }))
    .sort((a, b) => b.date.localeCompare(a.date));
};
